import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  LSEG_DISCLAIMER_TITLE,
  LSEG_DISCLAIMER_INTRO,
  LSEG_DISCLAIMER_INTRO_END,
  LSEG_DISCLAIMER_BODY,
  LSEG_WEBSITE_URL,
} from '@constants/lsegDisclaimer';
import './LsegDisclaimerDialog.less';

interface LsegDisclaimerDialogProps {
  returnFocusRef: React.RefObject<HTMLButtonElement>;
  onClose: () => void;
}

const LsegDisclaimerDialog: React.FC<LsegDisclaimerDialogProps> = ({ returnFocusRef, onClose }) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const trigger = returnFocusRef.current;
    const panel = panelRef.current;
    const focusTitle = () => titleRef.current?.focus({ preventScroll: true });
    focusTitle();

    const containFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !panel?.contains(event.target)) focusTitle();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      } else if (event.key === 'Tab') {
        const stops = Array.from(panel?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex="0"]',
        ) ?? []);
        if (!stops.length) return;
        const index = stops.indexOf(document.activeElement as HTMLElement);
        const next = index < 0
          ? (event.shiftKey ? stops.length - 1 : 0)
          : (index + (event.shiftKey ? -1 : 1) + stops.length) % stops.length;
        event.preventDefault();
        // Allow the scrollable body to reveal a focused link below the fold.
        stops[next].focus();
      }
    };
    document.addEventListener('focusin', containFocus);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('focusin', containFocus);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [onClose, returnFocusRef]);

  return createPortal(
    <div
      className="lseg-disclaimer"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="lseg-disclaimer__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        lang="ja"
      >
        <header className="lseg-disclaimer__header">
          <h2 ref={titleRef} id={titleId} className="lseg-disclaimer__title" tabIndex={-1}>
            {LSEG_DISCLAIMER_TITLE}
          </h2>
          <button className="lseg-disclaimer__close" type="button" onClick={onClose}>
            閉じる
          </button>
        </header>
        <div className="lseg-disclaimer__body" tabIndex={0} aria-label="免責事項本文">
          <p>
            {LSEG_DISCLAIMER_INTRO}
            <a href={LSEG_WEBSITE_URL} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
              {LSEG_WEBSITE_URL}
            </a>
            {LSEG_DISCLAIMER_INTRO_END}
          </p>
          <p>{LSEG_DISCLAIMER_BODY}</p>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LsegDisclaimerDialog;
